import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft, 
  Loader2,
  Heart,
  PartyPopper,
  Cake,
  GraduationCap,
  Gem,
  Sparkles,
  FileText,
  Clock,
  Mail,
  KeyRound,
  Save,
  LogOut,
  CheckCircle
} from "lucide-react";
import axios from "axios";
import { trackEmailVerified, trackSpeechGenerated } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const occasionIcons = {
  wedding: Heart,
  confirmation: Sparkles,
  birthday: Cake,
  student: GraduationCap,
  anniversary: Gem,
  other: PartyPopper
};

const SpeechForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // Start at 0 (email step)
  const [loading, setLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [occasions, setOccasions] = useState([]);
  const [tones, setTones] = useState([]);
  const [lengths, setLengths] = useState([]);
  const [customerEmail, setCustomerEmail] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [emailStep, setEmailStep] = useState("email"); // "email" or "code"
  const [verifyCode, setVerifyCode] = useState("");
  const [testCode, setTestCode] = useState(null);
  const [sendingCode, setSendingCode] = useState(false);
  // Track whether the user is "resuming" — i.e. came back from preview to edit
  const isResuming = typeof window !== "undefined"
    && new URLSearchParams(window.location.search).get("resume") === "1";
  
  const [formData, setFormData] = useState({
    occasion: "",
    recipient_name: "",
    speaker_name: "",
    speaker_relation: "",
    tone: "",
    length: "medium",
    anecdotes: ["", "", ""],
    personality_traits: "",
    favorite_quote: "",
    passion: "",
    wishes: "",
    // Wedding specific
    partner_name: "",
    how_they_met: "",
    proposal_story: "",
    what_makes_them_great: "",
    // Confirmation specific
    child_age: "",
    hobbies: "",
    childhood_story: "",
    proud_of: ""
  });

  const loadingMessages = [
    "Finder de helt rigtige ord...",
    "Væver dine anekdoter ind i talen...",
    "Justerer tonen så den rammer perfekt...",
    "Tilføjer det sidste drys humor...",
    "Sørger for at der ikke bliver et tørt øje...",
    "Dobbelttjekker at talen får dem til at grine OG græde...",
    "Polerer de sidste sætninger...",
    "Næsten klar — din tale er lige om hjørnet!"
  ];

  useEffect(() => {
    if (!loading) return;
    setLoadingMessageIndex(0);
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => 
        prev < loadingMessages.length - 1 ? prev + 1 : prev
      );
    }, 4000);
    return () => clearInterval(interval);
  }, [loading, loadingMessages.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [occasionsRes, tonesRes, lengthsRes] = await Promise.all([
          axios.get(`${API}/occasions`),
          axios.get(`${API}/tones`),
          axios.get(`${API}/lengths`)
        ]);
        setOccasions(occasionsRes.data);
        setTones(tonesRes.data);
        setLengths(lengthsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Kunne ikke hente data. Prøv igen.");
      }
    };
    fetchData();

    // Check if email is saved from previous session
    const saved = localStorage.getItem("mf_form_email");
    if (saved) {
      setCustomerEmail(saved);
      loadDraft(saved);
    }
  }, []);

  const loadDraft = async (email) => {
    try {
      const res = await axios.get(`${API}/drafts/load?email=${encodeURIComponent(email)}`);
      if (res.data.found) {
        setFormData(prev => ({ ...prev, ...res.data.form_data }));
        setStep(res.data.current_step);
        toast.success("Din gemte formular er hentet!");
      } else {
        setStep(1);
      }
    } catch {
      setStep(1);
    }
  };

  const saveDraft = async (nextStep) => {
    if (!customerEmail) return;
    try {
      await axios.post(`${API}/drafts/save`, {
        email: customerEmail,
        form_data: formData,
        current_step: nextStep
      });
    } catch {
      // Silent fail - draft saving is nice-to-have
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAnecdoteChange = (index, value) => {
    const newAnecdotes = [...formData.anecdotes];
    newAnecdotes[index] = value;
    setFormData(prev => ({ ...prev, anecdotes: newAnecdotes }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 0:
        return !!customerEmail && customerEmail.includes("@");
      case 1:
        return !!formData.occasion;
      case 2:
        return !!formData.tone && !!formData.length;
      case 3:
        return !!formData.recipient_name && !!formData.speaker_name && !!formData.speaker_relation;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (step === 0) return; // Step 0 handled by email/code flow
    if (!validateStep(step)) {
      toast.error("Udfyld venligst alle påkrævede felter");
      return;
    }
    const next = step + 1;
    setStep(next);
    saveDraft(next);
    window.scrollTo(0, 0);
  };

  const handleSendCode = async () => {
    if (!customerEmail || !customerEmail.includes("@")) {
      toast.error("Indtast en gyldig email");
      return;
    }
    setSendingCode(true);
    try {
      const res = await axios.post(`${API}/auth/send-code`, { email: customerEmail });
      setEmailStep("code");
      toast.success(res.data.message);
      if (res.data.test_code) setTestCode(res.data.test_code);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende kode");
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verifyCode.length !== 6) return;
    setSendingCode(true);
    try {
      await axios.post(`${API}/auth/verify-code`, { email: customerEmail, code: verifyCode });
      localStorage.setItem("mf_form_email", customerEmail.trim().toLowerCase());
      trackEmailVerified();
      toast.success("Email bekræftet!");
      loadDraft(customerEmail.trim().toLowerCase());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Forkert kode");
    } finally {
      setSendingCode(false);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSwitchEmail = () => {
    const confirmed = window.confirm(
      "Vil du skifte email? Din nuværende formular gemmes, men du vender tilbage til Step 0."
    );
    if (!confirmed) return;
    localStorage.removeItem("mf_form_email");
    setCustomerEmail("");
    setEmailStep("email");
    setVerifyCode("");
    setStep(0);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error("Udfyld venligst alle påkrævede felter");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        anecdotes: formData.anecdotes.filter(a => a.trim() !== ""),
        child_age: formData.child_age ? parseInt(formData.child_age) : null
      };

      const response = await axios.post(`${API}/speeches/generate`, submitData);

      trackSpeechGenerated(formData.length);

      // Save email association with this content for later login
      if (customerEmail) {
        localStorage.setItem("mf_checkout_email", customerEmail);
      }
      
      // Navigate to preview with content ID
      navigate(`/preview?content_ids=${response.data.id}`);
    } catch (error) {
      console.error("Error generating speech:", error);
      toast.error("Der opstod en fejl. Prøv igen senere.");
    } finally {
      setLoading(false);
    }
  };

  const selectedOccasion = occasions.find(o => o.id === formData.occasion);
  const selectedLength = lengths.find(l => l.id === formData.length);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="generating-overlay">
        <div className="text-center max-w-md px-4">
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="absolute inset-0 rounded-full border-4 border-muted" />
            <div className="absolute inset-0 rounded-full border-4 border-t-secondary animate-spin" />
            <Sparkles className="absolute inset-0 m-auto h-8 w-8 text-secondary" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-primary mb-3">
            Vi skriver din tale...
          </h2>
          <p className="text-muted-foreground mb-6 text-lg transition-all duration-500" data-testid="loading-message">
            {loadingMessages[loadingMessageIndex]}
          </p>
          <div className="bg-muted/50 rounded-full px-4 py-2 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            Ca. 30-60 sek. — godt tid til at øve dit bedste smil
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 md:py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {/* Progress indicator */}
        {step >= 1 && (
        <div className="mb-8">
          {/* Welcome back banner — shows verified email + auto-save indicator */}
          {customerEmail && (
            <div className="flex items-center justify-between gap-3 mb-4 bg-accent/10 border border-accent/30 rounded-lg px-4 py-2.5" data-testid="form-welcome-banner">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-primary truncate">
                    <span className="font-medium">{customerEmail}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground hidden sm:block">
                    Logget ind · Din formular gemmes automatisk
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleSwitchEmail}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 transition-colors flex-shrink-0"
                data-testid="form-switch-email"
                title="Skift email / log ud"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Skift email</span>
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Trin {step} af 4</span>
            <span className="text-sm text-muted-foreground">
              {step === 1 && "Vælg anledning"}
              {step === 2 && "Vælg tone og længde"}
              {step === 3 && "Personlige detaljer"}
              {step === 4 && "Anekdoter og historier"}
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-secondary transition-all duration-300"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>
        </div>
        )}

        {/* Step 0: Email verification */}
        {step === 0 && (
          <Card className="border-t-4 border-t-secondary animate-fadeInUp" data-testid="step-0">
            <CardContent className="p-6 md:p-8">
              {emailStep === "email" ? (
                <>
                  <div className="text-center mb-8">
                    <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
                      Lad os komme i gang
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Indtast din email så vi kan gemme din formular — du kan altid vende tilbage og fortsætte senere.
                    </p>
                  </div>
                  <div className="max-w-sm mx-auto">
                    <Input
                      type="email"
                      placeholder="din@email.dk"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="h-12 rounded-lg text-center text-lg"
                      data-testid="form-email-input"
                      onKeyDown={(e) => e.key === "Enter" && handleSendCode()}
                    />
                    <p className="text-xs text-muted-foreground text-center mt-3">
                      Vi sender en kode til din email for at bekræfte. Ingen spam, aldrig.
                    </p>
                    <div className="mt-4 flex items-start gap-2 bg-secondary/10 border border-secondary/30 rounded-lg p-3" data-testid="step-0-autosave-info">
                      <Save className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-primary">
                        💾 Vi gemmer automatisk dine svar undervejs — du kan altid lukke siden og vende tilbage senere.
                      </p>
                    </div>
                  </div>
                  <div className="mt-8 flex justify-center">
                    <Button
                      onClick={handleSendCode}
                      disabled={sendingCode || !customerEmail || !customerEmail.includes("@")}
                      className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-8 py-6 text-lg"
                      data-testid="step-0-send-code"
                    >
                      {sendingCode ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send kode <ChevronRight className="ml-2 h-5 w-5" /></>}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <KeyRound className="h-12 w-12 text-secondary mx-auto mb-4" />
                    <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
                      Indtast din kode
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Vi har sendt en 6-cifret kode til <strong>{customerEmail}</strong>
                    </p>
                  </div>
                  {testCode && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-center max-w-sm mx-auto" data-testid="test-code-display">
                      <p className="text-xs text-amber-600 mb-1">Test-tilstand — koden er:</p>
                      <p className="text-2xl font-bold text-amber-800 tracking-widest">{testCode}</p>
                    </div>
                  )}
                  <div className="max-w-sm mx-auto">
                    <Input
                      type="text"
                      placeholder="123456"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength={6}
                      className="h-14 rounded-lg text-center text-2xl tracking-[0.5em] font-mono"
                      data-testid="form-code-input"
                      onKeyDown={(e) => e.key === "Enter" && verifyCode.length === 6 && handleVerifyCode()}
                    />
                  </div>
                  <div className="mt-8 flex justify-center gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setEmailStep("email"); setVerifyCode(""); setTestCode(null); }}
                      className="rounded-full"
                    >
                      Skift email
                    </Button>
                    <Button
                      onClick={handleVerifyCode}
                      disabled={sendingCode || verifyCode.length !== 6}
                      className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-8 py-6 text-lg"
                      data-testid="step-0-verify"
                    >
                      {sendingCode ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Bekræft <ChevronRight className="ml-2 h-5 w-5" /></>}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 1: Occasion */}
        {step === 1 && (
          <Card className="border-t-4 border-t-secondary animate-fadeInUp" data-testid="step-1">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-8">
                <FileText className="h-12 w-12 text-primary mx-auto mb-4" />
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
                  Hvad er anledningen?
                </h1>
                <p className="text-muted-foreground mt-2">
                  Vælg den type begivenhed du skal holde tale til
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {occasions.map((occasion) => {
                  const Icon = occasionIcons[occasion.id] || PartyPopper;
                  return (
                    <button
                      key={occasion.id}
                      onClick={() => handleInputChange("occasion", occasion.id)}
                      data-testid={`occasion-${occasion.id}`}
                      className={`p-4 md:p-6 rounded-xl border-2 transition-all text-left hover-lift ${
                        formData.occasion === occasion.id
                          ? "border-secondary bg-secondary/10"
                          : "border-border hover:border-secondary/50"
                      }`}
                    >
                      <Icon className={`h-8 w-8 mb-3 ${
                        formData.occasion === occasion.id ? "text-secondary" : "text-primary/60"
                      }`} />
                      <h3 className="font-heading font-bold text-primary">
                        {occasion.name_da}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {occasion.description_da}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 flex justify-end">
                <Button
                  onClick={nextStep}
                  disabled={!formData.occasion}
                  className="bg-primary hover:bg-primary/90 rounded-full px-8"
                  data-testid="step-1-next"
                >
                  Næste <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Tone and Length */}
        {step === 2 && (
          <Card className="border-t-4 border-t-secondary animate-fadeInUp" data-testid="step-2">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-8">
                <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
                  Hvilken stemning?
                </h1>
                <p className="text-muted-foreground mt-2">
                  Vælg tonen og længden på din tale
                </p>
              </div>

              {/* Tone selection */}
              <div className="mb-8">
                <Label className="text-lg font-heading font-bold text-primary mb-4 block">
                  Talens tone
                </Label>
                <RadioGroup
                  value={formData.tone}
                  onValueChange={(value) => handleInputChange("tone", value)}
                  className="grid gap-3"
                >
                  {tones.map((tone) => (
                    <label
                      key={tone.id}
                      data-testid={`tone-${tone.id}`}
                      className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.tone === tone.id
                          ? "border-secondary bg-secondary/10"
                          : "border-border hover:border-secondary/50"
                      }`}
                    >
                      <RadioGroupItem value={tone.id} className="mr-4" />
                      <div>
                        <span className="font-medium text-primary">{tone.name_da}</span>
                        <p className="text-sm text-muted-foreground">{tone.description_da}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* Length selection */}
              <div className="mb-8">
                <Label className="text-lg font-heading font-bold text-primary mb-4 block">
                  Talens længde
                </Label>
                <RadioGroup
                  value={formData.length}
                  onValueChange={(value) => handleInputChange("length", value)}
                  className="grid gap-3"
                >
                  {lengths.map((length) => (
                    <label
                      key={length.id}
                      data-testid={`length-${length.id}`}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.length === length.id
                          ? "border-secondary bg-secondary/10"
                          : "border-border hover:border-secondary/50"
                      }`}
                    >
                      <div className="flex items-center">
                        <RadioGroupItem value={length.id} className="mr-4" />
                        <div>
                          <span className="font-medium text-primary">{length.name_da}</span>
                          <p className="text-sm text-muted-foreground">{length.description_da}</p>
                        </div>
                      </div>
                      <span className="font-bold text-secondary">{length.price_dkk} kr</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="rounded-full px-6"
                  data-testid="step-2-back"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" /> Tilbage
                </Button>
                <Button
                  onClick={nextStep}
                  disabled={!formData.tone || !formData.length}
                  className="bg-primary hover:bg-primary/90 rounded-full px-8"
                  data-testid="step-2-next"
                >
                  Næste <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Personal Details */}
        {step === 3 && (
          <Card className="border-t-4 border-t-secondary animate-fadeInUp" data-testid="step-3">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-8">
                <Heart className="h-12 w-12 text-primary mx-auto mb-4" />
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
                  Fortæl os om personen
                </h1>
                <p className="text-muted-foreground mt-2">
                  Disse oplysninger gør talen personlig
                </p>
              </div>

              <div className="space-y-6">
                {/* Basic fields */}
                <div>
                  <Label htmlFor="recipient_name" className="text-base font-medium">
                    Hvem er talen til? *
                  </Label>
                  <Input
                    id="recipient_name"
                    placeholder="F.eks. Anna, Thomas, Bedstemor"
                    value={formData.recipient_name}
                    onChange={(e) => handleInputChange("recipient_name", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-recipient-name"
                  />
                </div>

                {/* Wedding specific: Partner name */}
                {formData.occasion === "wedding" && (
                  <div>
                    <Label htmlFor="partner_name" className="text-base font-medium">
                      Partnerens navn
                    </Label>
                    <Input
                      id="partner_name"
                      placeholder="F.eks. Thomas (hvis talen er til Anna)"
                      value={formData.partner_name}
                      onChange={(e) => handleInputChange("partner_name", e.target.value)}
                      className="mt-2 h-12"
                      data-testid="input-partner-name"
                    />
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="speaker_name" className="text-base font-medium">
                      Dit navn (taleholder) *
                    </Label>
                    <Input
                      id="speaker_name"
                      placeholder="Dit fornavn"
                      value={formData.speaker_name}
                      onChange={(e) => handleInputChange("speaker_name", e.target.value)}
                      className="mt-2 h-12"
                      data-testid="input-speaker-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="speaker_relation" className="text-base font-medium">
                      Din relation *
                    </Label>
                    <Input
                      id="speaker_relation"
                      placeholder="F.eks. mor, bedste ven, kollega"
                      value={formData.speaker_relation}
                      onChange={(e) => handleInputChange("speaker_relation", e.target.value)}
                      className="mt-2 h-12"
                      data-testid="input-speaker-relation"
                    />
                  </div>
                </div>

                {/* Occasion specific fields */}
                {formData.occasion === "wedding" && (
                  <>
                    <div>
                      <Label htmlFor="how_they_met" className="text-base font-medium">
                        Hvordan mødte de hinanden?
                      </Label>
                      <Textarea
                        id="how_they_met"
                        placeholder="Fortæl kort historien om hvordan de mødte hinanden..."
                        value={formData.how_they_met}
                        onChange={(e) => handleInputChange("how_they_met", e.target.value)}
                        className="mt-2"
                        rows={3}
                        data-testid="input-how-they-met"
                      />
                    </div>
                    <div>
                      <Label htmlFor="proposal_story" className="text-base font-medium">
                        Frieriet (hvis relevant)
                      </Label>
                      <Textarea
                        id="proposal_story"
                        placeholder="Fortæl om frieriet hvis du kender historien..."
                        value={formData.proposal_story}
                        onChange={(e) => handleInputChange("proposal_story", e.target.value)}
                        className="mt-2"
                        rows={3}
                        data-testid="input-proposal-story"
                      />
                    </div>
                    <div>
                      <Label htmlFor="what_makes_them_great" className="text-base font-medium">
                        Hvad gør dem gode sammen?
                      </Label>
                      <Textarea
                        id="what_makes_them_great"
                        placeholder="Hvad er det der gør dem til et godt par?"
                        value={formData.what_makes_them_great}
                        onChange={(e) => handleInputChange("what_makes_them_great", e.target.value)}
                        className="mt-2"
                        rows={3}
                        data-testid="input-what-makes-them-great"
                      />
                    </div>
                  </>
                )}

                {formData.occasion === "confirmation" && (
                  <>
                    <div>
                      <Label htmlFor="child_age" className="text-base font-medium">
                        Konfirmandens alder
                      </Label>
                      <Input
                        id="child_age"
                        type="number"
                        placeholder="F.eks. 14"
                        value={formData.child_age}
                        onChange={(e) => handleInputChange("child_age", e.target.value)}
                        className="mt-2 h-12"
                        data-testid="input-child-age"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hobbies" className="text-base font-medium">
                        Interesser og hobbyer
                      </Label>
                      <Textarea
                        id="hobbies"
                        placeholder="Hvad interesserer konfirmanden sig for?"
                        value={formData.hobbies}
                        onChange={(e) => handleInputChange("hobbies", e.target.value)}
                        className="mt-2"
                        rows={3}
                        data-testid="input-hobbies"
                      />
                    </div>
                    <div>
                      <Label htmlFor="childhood_story" className="text-base font-medium">
                        En sjov historie fra barndommen
                      </Label>
                      <Textarea
                        id="childhood_story"
                        placeholder="Fortæl en sjov eller rørende historie fra barndommen..."
                        value={formData.childhood_story}
                        onChange={(e) => handleInputChange("childhood_story", e.target.value)}
                        className="mt-2"
                        rows={3}
                        data-testid="input-childhood-story"
                      />
                    </div>
                    <div>
                      <Label htmlFor="proud_of" className="text-base font-medium">
                        Hvad er du stolt af ved barnet?
                      </Label>
                      <Textarea
                        id="proud_of"
                        placeholder="Hvad gør dig stolt ved konfirmanden?"
                        value={formData.proud_of}
                        onChange={(e) => handleInputChange("proud_of", e.target.value)}
                        className="mt-2"
                        rows={3}
                        data-testid="input-proud-of"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-between mt-8">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="rounded-full px-6"
                  data-testid="step-3-back"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" /> Tilbage
                </Button>
                <Button
                  onClick={nextStep}
                  disabled={!formData.recipient_name || !formData.speaker_name || !formData.speaker_relation}
                  className="bg-primary hover:bg-primary/90 rounded-full px-8"
                  data-testid="step-3-next"
                >
                  Næste <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Anecdotes */}
        {step === 4 && (
          <Card className="border-t-4 border-t-secondary animate-fadeInUp" data-testid="step-4">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-8">
                <Sparkles className="h-12 w-12 text-primary mx-auto mb-4" />
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary">
                  Gør talen personlig
                </h1>
                <p className="text-muted-foreground mt-2">
                  Disse detaljer gør din tale unik og mindeværdig
                </p>
              </div>

              <div className="space-y-6">
                {/* Important note */}
                <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4" data-testid="anecdote-tip">
                  <p className="text-sm text-primary font-medium">
                    Jo mere du skriver, jo bedre bliver talen!
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Vores AI bruger dine historier til at gøre talen personlig. Uden detaljer vil den fylde ud med generelle formuleringer. Skriv gerne i stikordsform — vi klarer resten.
                  </p>
                </div>

                {/* Anecdotes */}
                <div>
                  <Label className="text-base font-medium mb-3 block">
                    Anekdoter eller sjove historier (valgfrit men anbefalet)
                  </Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Del 1-3 historier eller minder du gerne vil have med i talen. Skriv så mange detaljer som muligt.
                  </p>
                  {formData.anecdotes.map((anecdote, index) => (
                    <Textarea
                      key={index}
                      placeholder={`Historie ${index + 1}: F.eks. "Den gang vi var på ferie og..."`}
                      value={anecdote}
                      onChange={(e) => handleAnecdoteChange(index, e.target.value)}
                      className="mt-3"
                      rows={3}
                      data-testid={`input-anecdote-${index + 1}`}
                    />
                  ))}
                </div>

                <div>
                  <Label htmlFor="personality_traits" className="text-base font-medium">
                    Personens egenskaber
                  </Label>
                  <Textarea
                    id="personality_traits"
                    placeholder="Beskriv personen: F.eks. generøs, sjov, altid hjælpsom..."
                    value={formData.personality_traits}
                    onChange={(e) => handleInputChange("personality_traits", e.target.value)}
                    className="mt-2"
                    rows={2}
                    data-testid="input-personality-traits"
                  />
                </div>

                <div>
                  <Label htmlFor="favorite_quote" className="text-base font-medium">
                    Et citat eller vendning personen ofte bruger
                  </Label>
                  <Input
                    id="favorite_quote"
                    placeholder="F.eks. 'Det ordner sig altid!' eller 'Sådan er det bare'"
                    value={formData.favorite_quote}
                    onChange={(e) => handleInputChange("favorite_quote", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-favorite-quote"
                  />
                </div>

                <div>
                  <Label htmlFor="passion" className="text-base font-medium">
                    Noget personen er passioneret omkring
                  </Label>
                  <Input
                    id="passion"
                    placeholder="F.eks. haven, golf, børnebørnene, madlavning..."
                    value={formData.passion}
                    onChange={(e) => handleInputChange("passion", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-passion"
                  />
                </div>

                <div>
                  <Label htmlFor="wishes" className="text-base font-medium">
                    Ønsker eller budskab til personen
                  </Label>
                  <Textarea
                    id="wishes"
                    placeholder="Hvad vil du gerne ønske eller sige til personen?"
                    value={formData.wishes}
                    onChange={(e) => handleInputChange("wishes", e.target.value)}
                    className="mt-2"
                    rows={3}
                    data-testid="input-wishes"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="mt-8 p-4 bg-muted/50 rounded-xl">
                <h3 className="font-heading font-bold text-primary mb-2">Din tale</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>Anledning:</strong> {selectedOccasion?.name_da}</p>
                  <p><strong>Til:</strong> {formData.recipient_name}</p>
                  <p><strong>Fra:</strong> {formData.speaker_name} ({formData.speaker_relation})</p>
                  <p><strong>Længde:</strong> {selectedLength?.name_da}</p>
                  <p className="font-bold text-primary mt-2">
                    Pris: {selectedLength?.price_dkk} kr
                  </p>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  className="rounded-full px-6"
                  data-testid="step-4-back"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" /> Tilbage
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-8"
                  data-testid="generate-speech-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Genererer tale...
                    </>
                  ) : isResuming ? (
                    <>
                      Generer ny tale med ændringer <Sparkles className="ml-2 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Generer min tale <Sparkles className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SpeechForm;
